"use client";

import { Mail, MapPin, Phone } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

const Footer = () => {
  return (
    <footer className="bg-slate-800 px-4 text-slate-200">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="size-56 rounded-2xl border-2 border-slate-500 overflow-hidden">
              <Image
                src="/logo.png"
                alt="Kamal Associates Logo"
                width={500}
                height={500}
                className=" transition-transform hover:scale-105"
              />
            </div>
            <p className="text-slate-400 max-w-xs">
              Your trusted partner in property solutions, providing
              comprehensive real estate services since 2005.
            </p>
            <div className="flex space-x-4">
              <SocialLink
                href="https://www.facebook.com/kamal.associates1"
                icon="facebook"
              />
              <SocialLink
                href="https://www.instagram.com/kamal.associates/"
                icon="instagram"
              />
              <SocialLink
                href="https://in.linkedin.com/company/kamal-associates"
                icon="linkedin"
              />
              <SocialLink
                href="https://www.youtube.com/@KamalAssociates"
                icon="youtube"
              />
            </div>
          </div>

          {/* Information Links */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-wide">Information</h3>
            <ul className="space-y-4">
              <FooterLink href="/about" label="About Us" />
              <FooterLink href="/contact" label="Contact Us" />
              <FooterLink href="/careers" label="Careers" />
              <FooterLink href="/blogs" label="Blogs" />
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-wide">Services</h3>
            <ul className="space-y-4">
              <FooterLink
                href="https://www.kamaldocumentation.com/"
                label="Documentation"
              />
              <FooterLink
                href="https://www.garvikstay.com/"
                label="Garvik stay"
              />
              <FooterLink href="/services/home-loan" label="Home Loan" />
              <FooterLink
                href="/services/property-registration"
                label="Property Registration"
              />
              <FooterLink
                href="/services/property-legal"
                label="Legal Services"
              />
      
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:kamalassociatesbuilders@gmail.com"
                  className="flex items-center group"
                >
                  <Mail className="h-5 w-5 mr-3 text-slate-400 group-hover:text-white transition-colors" />
                  <span className="text-slate-400 group-hover:text-white transition-colors">
                    kamalassociatesbuilders@gmail.com
                  </span>
                </a>
              </li>
              <li>
                <a href="tel:+918448440765" className="flex items-center group">
                  <Phone className="h-5 w-5 mr-3 text-slate-400 group-hover:text-white transition-colors" />
                  <span className="text-slate-400 group-hover:text-white transition-colors">
                    +91-8448440765
                  </span>
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="h-5 w-5 mr-3 text-slate-400 mt-1 flex-shrink-0" />
                <span className="text-slate-400">
                  Dwarka Mor, Plot no. 123/124 sewak park Dwarka mor, Uttam
                  nagar 110059 metro pillar no. 783.{" "}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Kamal Associates — All rights reserved
          </p>
          <div className="flex space-x-6 mt-4 sm:mt-0">
            <Link
              href="/privacy"
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper components
const FooterLink = ({ href, label }) => (
  <li>
    <Link
      href={href}
      className="text-slate-400 hover:text-white transition-colors duration-200"
    >
      {label}
    </Link>
  </li>
);

const SocialLink = ({ href, icon }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-slate-800 hover:bg-slate-700 p-2 rounded-full transition-colors duration-200"
      aria-label={`Visit our ${icon} page`}
    >
      {icon === "facebook" && (
        <svg
          className="w-5 h-5 text-slate-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )}
      {icon === "instagram" && (
        <svg
          className="w-5 h-5 text-slate-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      )}
      {icon === "linkedin" && (
        <svg
          className="w-5 h-5 text-slate-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
        </svg>
      )}
      {icon === "youtube" && (
        <svg
          className="w-5 h-5 text-slate-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
        </svg>
      )}
    </a>
  );
};

export default Footer;
